import { normalizeRotation, type Size } from '../pdfPageLayout';

/**
 * Where a placed field sits, in the one coordinate space that survives
 * everything the reader can do to the page (FIL-8c).
 *
 * **Fields are stored in PDF user-space points against the *unrotated* page**,
 * never in screen pixels. Zoom changes pixels; rotate changes which pixels;
 * a window resize changes them again — and none of those is a change to where
 * the person put their signature. User space is also what `pdf-lib` draws in,
 * so saving needs no conversion beyond the y-axis flip that separates a
 * screen's origin from a page's.
 *
 * **All of it is pure**, for `pdfPageLayout.ts`'s reason: nothing lays out in
 * jsdom, so every rect is 0×0 and a rotation bug is invisible until somebody
 * opens a rotated filing. The arithmetic below is the part that can actually be
 * wrong, so it is the part that is tested.
 */

/** A field's box, in PDF points, origin bottom-left, against the unrotated page. */
export interface FieldRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Smallest a field may be dragged to, in points. Below this it cannot be grabbed. */
export const MIN_FIELD_POINTS = 18;

/**
 * How a page is currently being shown: the scale it is drawn at, the total
 * rotation applied to it, and its own size in points.
 */
export interface PageView {
  /**
   * CSS pixels per PDF point — the **committed** scale, and never the live one.
   *
   * A pinch magnifies the drawn column with a transform and commits a new scale
   * only when the fingers lift (ADR-0101), so for the length of a gesture what
   * is on screen is this number times the gesture's ratio. Every function below
   * is arithmetic against the resting layout; feeding a live scale in to "fix" a
   * mid-gesture drag would put a stored rect in a space nothing else uses.
   * `gesturing` is how the overlay knows to stand down instead.
   */
  scale: number;
  /** The page's own `/Rotate` plus whatever the reader turned it by. */
  rotation: number;
  /** The unrotated page box, in points. */
  page: Size;
  /**
   * Whether a pinch is running. Not part of the arithmetic below — it is here
   * because it travels with the view and the overlay is its only reader.
   */
  gesturing?: boolean;
}

/**
 * A rect in the page's own coordinates, as a box on screen.
 *
 * The overlay is absolutely positioned inside the same element the canvas
 * fills, so the answer is in CSS pixels from that element's top-left — which
 * means the y axis flips, because PDF counts up from the bottom.
 */
export function rectToScreen(rect: FieldRect, view: PageView): FieldRect {
  const { width: pageWidth, height: pageHeight } = view.page;
  const { x, y, width: w, height: h } = rect;

  /*
    **Both directions are written out in full, and neither is derived from the
    other.** The tidy-looking version — convert one way, and invert by calling
    the same function with `-rotation` — is what this module shipped first and
    it is wrong at every quarter turn: the inverse of "turn the page, then flip
    the y axis" is not "turn the page the other way, then flip the y axis",
    because the flip happens in the middle. It round-trips correctly at 0 and
    180, which is exactly the pair a casual test would try.

    So each case below is the composition of two things stated once: PDF counts
    up from the bottom-left, a screen counts down from the top-left, and a
    quarter turn swaps both the page's axes and the box's.
  */
  const turned: FieldRect =
    normalizeRotation(view.rotation) === 90
      ? { x: y, y: x, width: h, height: w }
      : normalizeRotation(view.rotation) === 180
        ? { x: pageWidth - x - w, y, width: w, height: h }
        : normalizeRotation(view.rotation) === 270
          ? { x: pageHeight - y - h, y: pageWidth - x - w, width: h, height: w }
          : { x, y: pageHeight - y - h, width: w, height: h };

  return {
    x: turned.x * view.scale,
    y: turned.y * view.scale,
    width: turned.width * view.scale,
    height: turned.height * view.scale,
  };
}

/** The inverse: a box the reader dragged, back into the page's own coordinates. */
export function rectFromScreen(box: FieldRect, view: PageView): FieldRect {
  const { width: pageWidth, height: pageHeight } = view.page;

  const sx = box.x / view.scale;
  const sy = box.y / view.scale;
  const sw = box.width / view.scale;
  const sh = box.height / view.scale;

  switch (normalizeRotation(view.rotation)) {
    case 90:
      return { x: sy, y: sx, width: sh, height: sw };
    case 180:
      return { x: pageWidth - sx - sw, y: sy, width: sw, height: sh };
    case 270:
      return { x: pageWidth - sy - sh, y: pageHeight - sx - sw, width: sh, height: sw };
    default:
      return { x: sx, y: pageHeight - sy - sh, width: sw, height: sh };
  }
}

/**
 * Keep a field on the page it belongs to.
 *
 * **Clamped rather than refused**, because the gesture that produces an
 * off-page rect is an ordinary drag that went a little too far — and a field
 * that silently vanished, or a drag that stopped responding at the edge, is
 * worse than one that stops moving. A field larger than the page is shrunk to
 * it rather than centred, so the reader can always see what they are holding.
 */
export function clampToPage(rect: FieldRect, page: Size): FieldRect {
  const width = Math.min(Math.max(rect.width, MIN_FIELD_POINTS), page.width);
  const height = Math.min(Math.max(rect.height, MIN_FIELD_POINTS), page.height);

  return {
    width,
    height,
    x: Math.min(Math.max(rect.x, 0), page.width - width),
    y: Math.min(Math.max(rect.y, 0), page.height - height),
  };
}

/**
 * A new field's box, centred on the point the reader clicked.
 *
 * `aspect` is the signature's own — a mark is placed at the shape it was drawn
 * in, and the reader resizes from there rather than being handed a square to
 * fix.
 */
export function fieldAt(
  point: { x: number; y: number },
  view: PageView,
  aspect: number,
  heightPoints: number,
): FieldRect {
  const height = heightPoints;
  const width = height * aspect;

  const centre = rectFromScreen({ x: point.x, y: point.y, width: 0, height: 0 }, view);

  return clampToPage(
    { x: centre.x - width / 2, y: centre.y - height / 2, width, height },
    view.page,
  );
}
